// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * NewsletterPreferencePage — public page accessible via email link
 * URL: /newsletter/preferences?uid=USER_ID  (no login needed)
 * Also handles unsubscribe: /newsletter/preferences?uid=USER_ID&unsubscribe=1
 */
export default function NewsletterPreferencePage() {
  const [topics, setTopics] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState('');
  const [isUnsubscribe, setIsUnsubscribe] = useState(false);
  const [existingPrefs, setExistingPrefs] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get('uid');
    const unsub = params.get('unsubscribe') === '1';
    setUserId(uid || '');
    setIsUnsubscribe(unsub);
    if (uid) {
      fetchData(uid);
    } else {
      setLoading(false);
      setError('Missing user ID. Please use the link from your email.');
    }
  }, []);

  const fetchData = async (uid) => {
    try {
      const [topicsRes, prefsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/newsletter/topics`),
        axios.get(`${BACKEND_URL}/api/newsletter/preferences/${uid}`).catch(() => ({ data: { selected_topics: [] } }))
      ]);
      setTopics(topicsRes.data.topics || []);
      const existing = prefsRes.data.selected_topics || [];
      setExistingPrefs(existing);
      setSelected(existing);
    } catch (e) {
      setError('Failed to load topics. Please try again.');
    }
    setLoading(false);
  };

  const toggleTopic = (slug) => {
    setSelected(prev =>
      prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : prev.length >= 6
        ? prev  // max 6
        : [...prev, slug]
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      setError('Please select at least 1 topic.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/preferences/${userId}`, {
        selected_topics: selected
      });
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const handleUnsubscribe = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all newsletters?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/preferences/${userId}`, { selected_topics: [] });
      setSaved(true);
    } catch (e) {
      setError('Failed to unsubscribe. Please email support@pricepalcompare.com');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
      </div>
    );
  }

  if (saved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="text-5xl mb-4">{isUnsubscribe ? '👋' : '✅'}</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isUnsubscribe ? 'Unsubscribed' : 'Preferences Saved!'}
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            {isUnsubscribe
              ? "You've been unsubscribed. We're sorry to see you go!"
              : `You'll receive personalised newsletters on: ${selected.map(s => topics.find(t => t.slug === s)?.title || s).join(', ')}`
            }
          </p>
          <a href="/app" className="inline-block bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition">
            Open PricePAL Compare
          </a>
        </div>
      </div>
    );
  }

  if (error && !topics.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 text-center mb-6 shadow-lg">
          <h1 className="text-2xl font-bold text-white mb-1">PricePAL Compare</h1>
          <p className="text-green-100 text-sm">Your Personalised Newsletter</p>
        </div>

        {/* Intro */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {existingPrefs.length > 0 ? '✏️ Update Your Topics' : '👋 Choose Your Newsletter Topics'}
          </h2>
          <p className="text-gray-600 text-sm">
            Select <strong>3–4 topics</strong> you care about and we'll send you a personalised newsletter with tips, deals, and insights tailored just for you.
          </p>
          {selected.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 rounded-full h-2 transition-all"
                  style={{ width: `${Math.min(selected.length / 4 * 100, 100)}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-600">{selected.length}/4 selected</span>
            </div>
          )}
        </div>

        {/* Topic Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {topics.map(topic => {
            const isSelected = selected.includes(topic.slug);
            return (
              <button
                key={topic.slug}
                onClick={() => toggleTopic(topic.slug)}
                className={`relative text-left rounded-2xl p-4 transition-all border-2 ${
                  isSelected
                    ? 'border-green-500 bg-green-50 shadow-md scale-[1.01]'
                    : 'border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                {/* Selected badge */}
                {isSelected && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    ✓
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="text-3xl flex-shrink-0">{topic.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-sm mb-0.5 ${isSelected ? 'text-green-700' : 'text-gray-800'}`}>
                      {topic.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-snug">{topic.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
            {error}
          </div>
        )}

        {/* Save button */}
        <div className="space-y-3">
          <button
            onClick={handleSave}
            disabled={saving || selected.length === 0}
            className="w-full bg-green-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
          >
            {saving ? (
              <><span className="animate-spin">⏳</span> Saving…</>
            ) : (
              `Save My ${selected.length} Topic${selected.length !== 1 ? 's' : ''} →`
            )}
          </button>

          <button
            onClick={handleUnsubscribe}
            className="w-full text-gray-400 text-sm hover:text-red-500 transition py-2"
          >
            Unsubscribe from all newsletters
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          PricePAL Compare Ltd · CRN: 17031232 · ICO: ZC094153<br/>
          We respect your privacy. You can change your preferences or unsubscribe at any time.
        </p>
      </div>
    </div>
  );
}
